import React, { useEffect, useState } from "react";
import api from "../services/api";

const AdminDashboard: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Add Gym Modal State
  const [showAddGym, setShowAddGym] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gymForm, setGymForm] = useState({ name: '', address: '', description: '', creditsPerHour: 5, maxPax: 40 });

  // Trainer Signup State
  const [showAddTrainer, setShowAddTrainer] = useState(false);
  const [trainerSubmitting, setTrainerSubmitting] = useState(false);
  const [trainerForm, setTrainerForm] = useState({ username: '', password: '' });
  const [trainerSignupResult, setTrainerSignupResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchStats = async (hideLoading = false) => {
      if (!hideLoading) setLoading(true);
      try {
        const response = await api.get('/admin/dashboard-stats');
        setData(response.data);
      } catch (err) {
        console.error("Dashboard fetch error", err);
      } finally {
        if (!hideLoading) setLoading(false);
      }
  };

  useEffect(() => {
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchStats(true), 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !data) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-lg text-gray-500 font-semibold">Loading Dashboard...</div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="text-red-500 font-semibold">Failed to load admin stats.</div></div>;

  const { stats, recentBookings, recentUsers, recentTrainers, allGyms } = data;

  return (
    <div className="h-[100dvh] overflow-y-auto w-full bg-[#F0F2F5] flex flex-col md:flex-row font-sans relative">
       <aside className="w-full md:w-64 bg-[#1C1C1E] text-white p-6 shrink-0 md:min-h-screen flex flex-col">
          <div className="flex items-center gap-3 mb-10">
              <div className="w-8 h-8 bg-[#D62422] rounded flex items-center justify-center font-bold text-white">F</div>
              <h2 className="text-2xl font-bold tracking-tight">Admin<span className="text-[#D62422]">Panel</span></h2>
          </div>
          <nav className="space-y-2 flex-1 hidden md:block">
            <button onClick={() => setActiveTab('overview')} className={`w-full text-left px-4 py-3 rounded transition-colors ${activeTab === 'overview' ? 'bg-[#D62422] text-white font-medium' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}>Overview</button>
            <button onClick={() => setActiveTab('bookings')} className={`w-full text-left px-4 py-3 rounded transition-colors ${activeTab === 'bookings' ? 'bg-[#D62422] text-white font-medium' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}>Bookings</button>
            <button onClick={() => setActiveTab('users')} className={`w-full text-left px-4 py-3 rounded transition-colors ${activeTab === 'users' ? 'bg-[#D62422] text-white font-medium' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}>Users & Trainers</button>
            <button onClick={() => setActiveTab('gyms')} className={`w-full text-left px-4 py-3 rounded transition-colors ${activeTab === 'gyms' ? 'bg-[#D62422] text-white font-medium' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}>Gym Management</button>
            <button onClick={() => setActiveTab('trainers')} className={`w-full text-left px-4 py-3 rounded transition-colors ${activeTab === 'trainers' ? 'bg-[#D62422] text-white font-medium' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}>Trainer Signup</button>
          </nav>
          
          <div className="mt-auto hidden md:block text-xs text-gray-500 pt-4 border-t border-gray-800">
             FitQuest Platform &copy; 2026
          </div>
       </aside>

       <main className="flex-1 p-4 md:p-8 w-full pb-20 md:pb-8">
          <div className="max-w-7xl mx-auto">
             
             {activeTab === 'overview' && (
                 <>
                    <h1 className="text-3xl font-bold mb-6 text-gray-900 border-b pb-4">Platform Overview</h1>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
                        <StatCard title="Total Revenue" value={`$${stats.estimatedRevenue}`} color="text-green-600" />
                        <StatCard title="Active Bookings" value={stats.confirmedBookings + stats.pendingBookings} color="text-blue-600" />
                        <StatCard title="Total Users" value={stats.totalUsers} />
                        <StatCard title="Total Trainers" value={stats.totalTrainers} />
                        <StatCard title="Gym Locations" value={stats.totalGyms} />
                        <StatCard title="Credits Spent" value={stats.totalCreditsUsed} />
                        <StatCard title="Pending" value={stats.pendingBookings} color="text-yellow-600" />
                        <StatCard title="Cancelled" value={stats.cancelledBookings} color="text-red-500" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center justify-between">
                                Recent Bookings 
                                <span className="text-sm font-normal text-[#D62422] cursor-pointer hover:underline" onClick={() => setActiveTab('bookings')}>View All</span>
                            </h2>
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                               <BookingTable bookings={recentBookings.slice(0, 5)} />
                            </div>
                        </div>

                        <div className="space-y-8">
                            <div>
                                <h2 className="text-xl font-bold mb-4 text-gray-800">Newest Signups</h2>
                                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden divide-y">
                                    {recentUsers.map((u:any, i:number) => (
                                        <div key={i} className="p-4 flex items-center justify-between hover:bg-gray-50">
                                            <div>
                                                <div className="font-semibold text-sm text-gray-900">{u.name}</div>
                                                <div className="text-xs text-gray-500">{u.email}</div>
                                            </div>
                                            <div className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 font-medium">{new Date(u.createdAt).toLocaleDateString()}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div>
                                <h2 className="text-xl font-bold mb-4 text-gray-800">Premium Gyms</h2>
                                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                                    {allGyms.map((g:any, i:number) => (
                                        <div key={i} className="border-b last:border-0 py-3 flex justify-between items-center">
                                            <div className="text-sm font-medium text-gray-800">{g.name}</div>
                                            <div className="text-xs px-2 py-1 bg-red-50 text-red-700 font-bold rounded">{g.creditsPerHour} CR/hr</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                 </>
             )}

             {activeTab === 'bookings' && (
                 <>
                    <h1 className="text-3xl font-bold mb-6 text-gray-900 border-b pb-4">All Recent Bookings</h1>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <BookingTable bookings={recentBookings} />
                    </div>
                 </>
             )}

             {activeTab === 'users' && (
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                     <div>
                        <h1 className="text-2xl font-bold mb-6 text-gray-900 border-b pb-4">Recent Users</h1>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden divide-y">
                            {recentUsers.map((u:any, i:number) => (
                                <div key={i} className="p-4 hover:bg-gray-50">
                                    <div className="flex justify-between items-start mb-1">
                                        <div className="font-bold text-gray-900">{u.name}</div>
                                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium">{u.credits} Credits</span>
                                    </div>
                                    <div className="text-sm text-gray-500">{u.email}</div>
                                    <div className="text-xs text-gray-400 mt-2">ID: {u._id}</div>
                                </div>
                            ))}
                        </div>
                     </div>
                     <div>
                        <h1 className="text-2xl font-bold mb-6 text-gray-900 border-b pb-4">Recent Trainers</h1>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden divide-y">
                            {recentTrainers.map((t:any, i:number) => (
                                <div key={i} className="p-4 hover:bg-gray-50">
                                    <div className="flex justify-between items-start mb-1">
                                        <div className="font-bold text-gray-900">{t.name}</div>
                                        <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded font-medium">{t.creditsPerHour} / hr</span>
                                    </div>
                                    <div className="text-sm text-gray-500">{t.email}</div>
                                    <div className="text-xs text-gray-400 mt-2">ID: {t._id}</div>
                                </div>
                            ))}
                        </div>
                     </div>
                 </div>
             )}

             {activeTab === 'gyms' && (
                 <>
                    <div className="flex justify-between items-center mb-6 border-b pb-4">
                        <h1 className="text-3xl font-bold text-gray-900">Gym Locations</h1>
                        <button onClick={() => setShowAddGym(true)} className="bg-[#D62422] hover:bg-red-700 text-white px-4 py-2 rounded font-bold text-sm shadow-sm transition-colors flex items-center gap-2 cursor-pointer">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg> Add Gym
                        </button>
                    </div>

                    {showAddGym && (
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8 relative">
                           <button onClick={() => setShowAddGym(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg></button>
                           <h2 className="text-xl font-bold mb-4 text-gray-800">Add New Gym</h2>
                           <form onSubmit={async (e) => {
                               e.preventDefault();
                               setIsSubmitting(true);
                               try {
                                   await api.post('/admin/gym', gymForm);
                                   await fetchStats(true); // silently reload stats
                                   setShowAddGym(false);
                                   setGymForm({ name: '', address: '', description: '', creditsPerHour: 5, maxPax: 40 });
                               } catch(err) {
                                   console.error("Gym Add Error", err);
                               } finally {
                                   setIsSubmitting(false);
                               }
                           }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Gym Name</label>
                                    <input type="text" required value={gymForm.name} onChange={e => setGymForm({...gymForm, name: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded p-2 text-sm focus:border-[#D62422] outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Address Location</label>
                                    <input type="text" required value={gymForm.address} onChange={e => setGymForm({...gymForm, address: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded p-2 text-sm focus:border-[#D62422] outline-none" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Description</label>
                                    <textarea value={gymForm.description} onChange={e => setGymForm({...gymForm, description: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded p-2 text-sm focus:border-[#D62422] outline-none min-h-[80px]" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Credits Per Hour</label>
                                    <input type="number" required value={gymForm.creditsPerHour} onChange={e => setGymForm({...gymForm, creditsPerHour: parseInt(e.target.value)})} className="w-full bg-gray-50 border border-gray-200 rounded p-2 text-sm focus:border-[#D62422] outline-none" min="1" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Maximum Pax Capacity</label>
                                    <input type="number" required value={gymForm.maxPax} onChange={e => setGymForm({...gymForm, maxPax: parseInt(e.target.value)})} className="w-full bg-gray-50 border border-gray-200 rounded p-2 text-sm focus:border-[#D62422] outline-none" min="1" />
                                </div>
                                <div className="md:col-span-2 flex justify-end mt-2">
                                    <button disabled={isSubmitting} className="bg-gray-900 text-white px-6 py-2 rounded font-bold shadow hover:bg-gray-800 disabled:opacity-50">
                                        {isSubmitting ? 'Saving...' : 'Save Gym'}
                                    </button>
                                </div>
                           </form>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {allGyms.map((g:any, i:number) => (
                            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                                <div className="bg-gray-100 h-32 flex items-center justify-center relative">
                                    <span className="absolute top-2 right-2 bg-white text-xs px-2 py-1 rounded shadow-sm font-bold">{g.creditsPerHour} CR/hr</span>
                                    <svg className="w-12 h-12 text-gray-300" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"/></svg>
                                </div>
                                <div className="p-5 flex-1 flex flex-col justify-between">
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-900 mb-2">{g.name}</h3>
                                        <p className="text-sm text-gray-500 line-clamp-2 mb-4">{g.address?.street || 'No Street'}, {g.address?.city}</p>
                                    </div>
                                    <div className="flex items-center text-sm font-semibold text-gray-700 border-t pt-3">
                                        <svg className="w-4 h-4 mr-1 text-[#D62422]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                                        Max Pax: {g.maxPax || 1}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                 </>
             )}

             {activeTab === 'trainers' && (
                 <>
                    <div className="flex justify-between items-center mb-6 border-b pb-4">
                        <h1 className="text-3xl font-bold text-gray-900">Trainer Management</h1>
                        <button onClick={() => { setShowAddTrainer(true); setTrainerSignupResult(null); }} className="bg-[#D62422] hover:bg-red-700 text-white px-4 py-2 rounded font-bold text-sm shadow-sm transition-colors flex items-center gap-2 cursor-pointer">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/></svg> Add Trainer
                        </button>
                    </div>

                    {showAddTrainer && (
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8 relative max-w-lg">
                           <button onClick={() => { setShowAddTrainer(false); setTrainerSignupResult(null); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg></button>
                           <h2 className="text-xl font-bold mb-1 text-gray-800">Register New Trainer</h2>
                           <p className="text-sm text-gray-500 mb-5">Create login credentials for a trainer. They can log in and set up their profile afterwards.</p>

                           {trainerSignupResult && (
                               <div className={`mb-4 px-4 py-3 rounded text-sm font-medium ${
                                   trainerSignupResult.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                               }`}>
                                   {trainerSignupResult.message}
                               </div>
                           )}

                           <form onSubmit={async (e) => {
                               e.preventDefault();
                               setTrainerSubmitting(true);
                               setTrainerSignupResult(null);
                               try {
                                   await api.post('/trainer/signup', {
                                       username: trainerForm.username,
                                       password: trainerForm.password
                                   });
                                   setTrainerSignupResult({ type: 'success', message: `Trainer account created for ${trainerForm.username}` });
                                   setTrainerForm({ username: '', password: '' });
                                   fetchStats(true);
                               } catch(err: any) {
                                   const msg = err?.response?.data?.message || err?.response?.data || 'Failed to create trainer account. Check password requirements.';
                                   setTrainerSignupResult({ type: 'error', message: typeof msg === 'string' ? msg : JSON.stringify(msg) });
                               } finally {
                                   setTrainerSubmitting(false);
                               }
                           }} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Email (Username)</label>
                                    <input type="email" required value={trainerForm.username} onChange={e => setTrainerForm({...trainerForm, username: e.target.value})} placeholder="trainer@fitquestsg.com" className="w-full bg-gray-50 border border-gray-200 rounded p-3 text-sm focus:border-[#D62422] outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Password</label>
                                    <input type="password" required value={trainerForm.password} onChange={e => setTrainerForm({...trainerForm, password: e.target.value})} placeholder="Min 8 chars, upper+lower+number+special" className="w-full bg-gray-50 border border-gray-200 rounded p-3 text-sm focus:border-[#D62422] outline-none" />
                                    <p className="text-xs text-gray-400 mt-1">Must contain: 8+ chars, uppercase, lowercase, number, and special character (@$!%*?&)</p>
                                </div>
                                <div className="flex justify-end pt-2">
                                    <button disabled={trainerSubmitting} className="bg-gray-900 text-white px-6 py-2.5 rounded font-bold shadow hover:bg-gray-800 disabled:opacity-50 text-sm">
                                        {trainerSubmitting ? 'Creating...' : 'Create Trainer Account'}
                                    </button>
                                </div>
                           </form>
                        </div>
                    )}

                    <h2 className="text-xl font-bold mb-4 text-gray-800">Registered Trainers</h2>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden divide-y">
                        {recentTrainers.length > 0 ? recentTrainers.map((t:any, i:number) => (
                            <div key={i} className="p-4 hover:bg-gray-50 flex justify-between items-center">
                                <div>
                                    <div className="font-bold text-gray-900">{t.name || 'Unnamed Trainer'}</div>
                                    <div className="text-sm text-gray-500">{t.email || 'No email set'}</div>
                                    <div className="text-xs text-gray-400 mt-1">ID: {t._id}</div>
                                </div>
                                <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded font-medium">{t.creditsPerHour || 0} CR/hr</span>
                            </div>
                        )) : (
                            <div className="p-8 text-center text-sm text-gray-500">No trainers registered yet</div>
                        )}
                    </div>
                 </>
             )}
          </div>
       </main>

       {/* Mobile Tab Nav (Bottom) */}
       <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#1C1C1E] flex border-t border-gray-800 px-2 py-3 justify-around z-50">
          <button onClick={() => setActiveTab('overview')} className={`flex-1 text-center text-xs font-semibold ${activeTab==='overview'?'text-[#D62422]':'text-gray-400'}`}>Overview</button>
          <button onClick={() => setActiveTab('bookings')} className={`flex-1 text-center text-xs font-semibold ${activeTab==='bookings'?'text-[#D62422]':'text-gray-400'}`}>Bookings</button>
          <button onClick={() => setActiveTab('users')} className={`flex-1 text-center text-xs font-semibold ${activeTab==='users'?'text-[#D62422]':'text-gray-400'}`}>Users</button>
          <button onClick={() => setActiveTab('gyms')} className={`flex-1 text-center text-xs font-semibold ${activeTab==='gyms'?'text-[#D62422]':'text-gray-400'}`}>Gyms</button>
          <button onClick={() => setActiveTab('trainers')} className={`flex-1 text-center text-xs font-semibold ${activeTab==='trainers'?'text-[#D62422]':'text-gray-400'}`}>Trainers</button>
       </div>
    </div>
  );
};

const StatCard = ({ title, value, color }: { title: string; value: any, color?: string }) => (
  <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center">
    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{title}</div>
    <div className={`text-3xl font-black ${color || 'text-gray-900'}`}>{value}</div>
  </div>
);

const BookingTable = ({ bookings }: { bookings: any[] }) => (
    <div className="overflow-x-auto">
        <table className="min-w-full">
            <thead className="bg-[#FAFAFA] border-b border-gray-200">
                <tr>
                <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Date/Time</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Target</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Cost</th>
                <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
                {bookings.map((b: any, i: number) => (
                <tr key={i} className="hover:bg-blue-50/50 transition-colors">
                    <td className="px-5 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{new Date(b.startDateTime).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-500">{new Date(b.startDateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-800">{b.userId?.name || 'Unknown'}</div>
                        <div className="text-xs text-gray-500">{b.userId?.email || 'No email'}</div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                        {b.gymId ? (
                             <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-[#D62422]/10 text-[#D62422]">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1v1H9V7zm5 0h1v1h-1V7zm-5 4h1v1H9v-1zm5 0h1v1h-1v-1zm-5 4h1v1H9v-1zm5 0h1v1h-1v-1z"/></svg>
                                {b.gymId.name}
                             </span>
                        ) : b.trainerId ? (
                             <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-blue-100 text-blue-700">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                                {b.trainerId.name}
                             </span>
                        ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                        )}
                        {b.numberOfPax > 1 && <div className="text-[10px] text-gray-400 mt-1 font-semibold">{b.numberOfPax} Pax</div>}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                        {b.creditsUsed} CR
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-1 inline-flex text-[11px] leading-4 font-bold rounded shadow-sm border ${
                            b.status === 'confirmed' ? 'bg-green-50 text-green-700 border-green-200' :
                            b.status === 'cancelled' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                        }`}>
                            {b.status.toUpperCase()}
                        </span>
                    </td>
                </tr>
                ))}
                {bookings.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-500 font-medium">No recent bookings found</td></tr>
                )}
            </tbody>
        </table>
    </div>
);

export default AdminDashboard;
